// =============================================================================
// Dashboard UI Utilities
// Extracted from make_album/dashboard.html inline JS
// =============================================================================

// -----------------------------------------------------------------------------
// Toast notification
// -----------------------------------------------------------------------------

interface ToastOptions {
  red?: boolean;
}

export function showToast(msg: string, opts?: ToastOptions): void {
  const toast = document.createElement('div');
  const bg = (opts && opts.red) ? '#e53e3e' : '#11110f';
  toast.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);' +
    'background:' + bg + ';color:#fff;padding:10px 24px;border-radius:20px;' +
    'font-size:13px;z-index:2000;box-shadow:var(--shadow-raised-hover);';
  toast.textContent = msg;
  document.body.appendChild(toast);
  setTimeout(function() { toast.remove(); }, 2500);
}

// -----------------------------------------------------------------------------
// Prompt modal
// -----------------------------------------------------------------------------

export function showPrompt(title: string, defaultValue?: string, isPassword?: boolean): Promise<string | null> {
  return new Promise(function(resolve) {
    document.getElementById('promptTitle')!.textContent = title;
    const input = document.getElementById('promptInput') as HTMLInputElement;
    input.value = defaultValue || '';
    input.type = isPassword ? 'password' : 'text';
    document.getElementById('promptMessage')!.style.display = 'none';
    document.getElementById('promptModalOverlay')!.classList.remove('hidden');
    input.focus();

    function cleanup(): void {
      document.getElementById('promptModalOverlay')!.classList.add('hidden');
      document.getElementById('promptCancel')!.removeEventListener('click', onCancel);
      document.getElementById('promptConfirm')!.removeEventListener('click', onConfirm);
      document.getElementById('promptModalClose')!.removeEventListener('click', onCancel);
      document.getElementById('promptModalOverlay')!.removeEventListener('click', onOverlay);
    }

    function onCancel(): void { cleanup(); resolve(null); }
    function onConfirm(): void { cleanup(); resolve(input.value); }
    function onOverlay(e: MouseEvent): void { if (e.target === document.getElementById('promptModalOverlay')) { onCancel(); } }

    document.getElementById('promptCancel')!.addEventListener('click', onCancel);
    document.getElementById('promptConfirm')!.addEventListener('click', onConfirm);
    document.getElementById('promptModalClose')!.addEventListener('click', onCancel);
    document.getElementById('promptModalOverlay')!.addEventListener('click', onOverlay);
    document.getElementById('promptInput')!.addEventListener('keydown', function(e: KeyboardEvent) {
      if (e.key === 'Enter') onConfirm();
    });
  });
}

// -----------------------------------------------------------------------------
// Confirm modal
// -----------------------------------------------------------------------------

export function showConfirm(title: string, message: string): Promise<boolean> {
  return new Promise(function(resolve) {
    document.getElementById('confirmTitle')!.textContent = title;
    document.getElementById('confirmMessage')!.textContent = message;
    document.getElementById('confirmModalOverlay')!.classList.remove('hidden');

    function cleanup(): void {
      document.getElementById('confirmModalOverlay')!.classList.add('hidden');
      document.getElementById('confirmCancel')!.removeEventListener('click', onCancel);
      document.getElementById('confirmOk')!.removeEventListener('click', onOk);
      document.getElementById('confirmModalClose')!.removeEventListener('click', onCancel);
      document.getElementById('confirmModalOverlay')!.removeEventListener('click', onOverlay);
    }

    function onCancel(): void { cleanup(); resolve(false); }
    function onOk(): void { cleanup(); resolve(true); }
    function onOverlay(e: MouseEvent): void { if (e.target === document.getElementById('confirmModalOverlay')) { onCancel(); } }

    document.getElementById('confirmCancel')!.addEventListener('click', onCancel);
    document.getElementById('confirmOk')!.addEventListener('click', onOk);
    document.getElementById('confirmModalClose')!.addEventListener('click', onCancel);
    document.getElementById('confirmModalOverlay')!.addEventListener('click', onOverlay);
  });
}

// -----------------------------------------------------------------------------
// Warning modal
// -----------------------------------------------------------------------------

export function showWarning(message: string): void {
  document.getElementById('warnMessage')!.textContent = message;
  document.getElementById('warnModalOverlay')!.classList.remove('hidden');

  function cleanup(): void {
    document.getElementById('warnModalOverlay')!.classList.add('hidden');
    document.getElementById('warnOk')!.removeEventListener('click', cleanup);
  }

  document.getElementById('warnOk')!.addEventListener('click', cleanup);
}

// -----------------------------------------------------------------------------
// HTML escaping
// -----------------------------------------------------------------------------

export function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// -----------------------------------------------------------------------------
// Blob / Image URL utilities
// -----------------------------------------------------------------------------

export function readBlobAsDataUrl(blob: Blob): Promise<string> {
  return new Promise(function(resolve, reject) {
    const reader = new FileReader();
    reader.onload = function(e) { resolve((e.target as FileReader).result as string); };
    reader.onerror = function() { reject(reader.error || new Error('FileReader failed')); };
    reader.readAsDataURL(blob);
  });
}

export function canInlineImageSrc(src: string | null | undefined): boolean {
  return !!src && !/^data:/i.test(src) && !/^about:/i.test(src);
}

export async function imageSrcToDataUrl(src: string): Promise<string> {
  const resp = /^blob:/i.test(src) ? await fetch(src) : await fetch(src, { mode: 'cors', credentials: 'omit' });
  if (!resp.ok) throw new Error('HTTP ' + resp.status);
  return readBlobAsDataUrl(await resp.blob());
}

// -----------------------------------------------------------------------------
// Time formatting
// -----------------------------------------------------------------------------

export function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.getFullYear() + '/' + String(d.getMonth() + 1).padStart(2, '0') + '/' + String(d.getDate()).padStart(2, '0') + ' ' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}

// -----------------------------------------------------------------------------
// Order ID generation
// -----------------------------------------------------------------------------

export function genOrderId(): string {
  const now = new Date();
  return 'ORD' + now.getFullYear() +
    String(now.getMonth() + 1).padStart(2, '0') +
    String(now.getDate()).padStart(2, '0') +
    String(now.getHours()).padStart(2, '0') +
    String(now.getMinutes()).padStart(2, '0') +
    String(now.getSeconds()).padStart(2, '0');
}
